'use client';

import React from 'react';
import styled from 'styled-components';

/**
 * CounterDisplay Component Props
 */
export interface CounterDisplayProps {
  /** The numeric count to display */
  count: number;
  /** Label text displayed below the count */
  label: string;
  /** Enable animated count transitions */
  animated?: boolean;
  /** Optional click handler */
  onClick?: () => void;
  /** Optional CSS class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.2s ease;

  ${({ $clickable }) =>
    $clickable &&
    `
    &:hover {
      transform: scale(1.05);
    }
    &:active {
      transform: scale(0.98);
    }
  `}
`;

const CountNumber = styled.div<{ $animated?: boolean }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  background: linear-gradient(270deg, #D33324 0%, #FB6F2A 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 20px rgba(251, 111, 42, 0.3);
  margin-bottom: 8px;
  
  ${({ $animated }) =>
    $animated &&
    `
    transition: all 0.3s ease;
  `}

  @media (max-width: 768px) {
    font-size: 40px;
  }

  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #b0b0b0;
  text-align: center;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

// ============================================================================
// Component
// ============================================================================

/**
 * CounterDisplay Component
 * 
 * Displays a large numeric counter with gradient styling and optional animation.
 * Perfect for showing metrics, counts, or statistics.
 * 
 * @component
 * @example
 * ```tsx
 * <CounterDisplay
 *   count={11}
 *   label="Active Alert Patterns"
 * />
 * 
 * // With click handler
 * <CounterDisplay
 *   count={42}
 *   label="Total Logs Today"
 *   onClick={() => console.log('Counter clicked')}
 *   animated
 * />
 * ```
 */
export const CounterDisplay: React.FC<CounterDisplayProps> = ({
  count,
  label,
  animated = false,
  onClick,
  className,
}) => {
  return (
    <Container
      $clickable={!!onClick}
      onClick={onClick}
      className={className}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <CountNumber $animated={animated}>{count.toLocaleString()}</CountNumber>
      <Label>{label}</Label>
    </Container>
  );
};

export default CounterDisplay;

